import {
  FormHelperText,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
} from '@material-ui/icons';
import Placeholder from '@tiptap/extension-placeholder';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import * as React from 'react';

const useStyles = makeStyles((theme) => ({
  label: {
    display: 'block',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
  },
  box: {
    display: 'flex',
    alignItems: 'flex-start',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: theme.shape.borderRadius,
    transition: 'border-color 150ms ease',
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
  },
  boxFocused: {
    borderColor: `${theme.palette.primary.main} !important`,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  boxDisabled: {
    borderColor: 'rgba(0, 0, 0, 0.12) !important',
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  toolbar: {
    display: 'flex',
    gap: 2,
    padding: theme.spacing(0.5, 0.5, 0),
  },
  toolButton: {
    padding: 4,
    borderRadius: theme.shape.borderRadius,
  },
  toolButtonActive: {
    backgroundColor: theme.palette.action.selected,
  },
  content: {
    ...theme.typography.body1,
    padding: theme.spacing(1.25, 1.75),
    '& .ProseMirror': {
      outline: 'none',
      minHeight: '1.5em',
      wordBreak: 'break-word',
    },
    '& p': {
      margin: 0,
    },
    '& ul, & ol': {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(3),
    },
    '& p.is-editor-empty:first-child::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      height: 0,
      pointerEvents: 'none',
      color: theme.palette.text.disabled,
    },
  },
  contentDisabled: {
    color: theme.palette.text.disabled,
  },
  adornment: {
    alignSelf: 'center',
    paddingRight: theme.spacing(1),
  },
}));

// Comments saved before the editor existed are plain text
const isHtml = (value: string) => /<[a-z][\s\S]*>/i.test(value);

const toEditorHtml = (value?: string) => {
  if (!value) return '';
  if (isHtml(value)) return value;
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return value
    .split('\n')
    .map((line) => `<p>${escape(line)}</p>`)
    .join('');
};

const toolbarItems = [
  {
    title: 'Bold',
    icon: FormatBold,
    mark: 'bold',
    run: (e: Editor) => e.chain().focus().toggleBold().run(),
  },
  {
    title: 'Italic',
    icon: FormatItalic,
    mark: 'italic',
    run: (e: Editor) => e.chain().focus().toggleItalic().run(),
  },
  {
    title: 'Bulleted list',
    icon: FormatListBulleted,
    mark: 'bulletList',
    run: (e: Editor) => e.chain().focus().toggleBulletList().run(),
  },
  {
    title: 'Numbered list',
    icon: FormatListNumbered,
    mark: 'orderedList',
    run: (e: Editor) => e.chain().focus().toggleOrderedList().run(),
  },
];

interface Props {
  value?: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  helperText?: string;
  autoFocus?: boolean;
  endAdornment?: React.ReactNode;
  className?: string;
}

export const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder,
  label,
  helperText,
  autoFocus,
  endAdornment,
  className,
}) => {
  const classes = useStyles();
  const [focused, setFocused] = React.useState(false);
  // Keep the latest callback without re-creating the editor
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: toEditorHtml(value),
    editable: !disabled,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      onChangeRef.current(editor.isEmpty ? '' : editor.getHTML());
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });

  React.useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Follow outside changes, e.g. the box being cleared after sending
  React.useEffect(() => {
    if (!editor) return;
    if (!value ? editor.isEmpty : value === editor.getHTML()) return;
    editor.commands.setContent(toEditorHtml(value), false);
  }, [editor, value]);

  return (
    <div className={className}>
      {label && (
        <Typography variant="caption" className={classes.label}>
          {label}
        </Typography>
      )}
      <div
        className={`${classes.box} ${focused ? classes.boxFocused : ''} ${
          disabled ? classes.boxDisabled : ''
        }`}
      >
        <div className={classes.main}>
          {focused && editor && !disabled && (
            <div className={classes.toolbar}>
              {toolbarItems.map((item) => (
                <Tooltip key={item.title} title={item.title}>
                  <IconButton
                    size="small"
                    aria-label={item.title}
                    aria-pressed={editor.isActive(item.mark)}
                    className={`${classes.toolButton} ${
                      editor.isActive(item.mark) ? classes.toolButtonActive : ''
                    }`}
                    // Keep focus in the editor so the toolbar stays open
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => item.run(editor)}
                  >
                    <item.icon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ))}
            </div>
          )}
          <EditorContent
            editor={editor}
            className={`${classes.content} ${
              disabled ? classes.contentDisabled : ''
            }`}
          />
        </div>
        {endAdornment && (
          <div className={classes.adornment}>{endAdornment}</div>
        )}
      </div>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </div>
  );
};
